import { ITask, TaskStatus } from 'admiral-core';

/**
 * Order tasks within one status column: manual / server drag order (`statusSortOrder`), then
 * newer `createdAt` first. COMPLETED and ARCHIVED use the same rule so “Manuell sortiert” works.
 */
export function compareTasksByStatusOrder(a: ITask, b: ITask): number {
  const ao = a.statusSortOrder ?? 0;
  const bo = b.statusSortOrder ?? 0;
  if (ao !== bo) return ao - bo;
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

/**
 * Uses direct children of the Sortable root (vuedraggable `tag="ul"` → `li` rows).
 * Avoids collecting nested `[data-task-id]` and matches visual row order.
 */
export function collectTaskIdsFromListEl(listEl: HTMLElement): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (let i = 0; i < listEl.children.length; i++) {
    const child = listEl.children[i] as HTMLElement;
    const id =
      child.getAttribute('data-task-id') ||
      child.querySelector('[data-task-id]')?.getAttribute('data-task-id');
    if (id && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

/** Sortable's drag element may be the row `<li>`; querySelector does not match the element itself. */
export function getDraggedTaskIdFromSortableItem(item: HTMLElement | null): string | null {
  if (!item) return null;
  const self = item.getAttribute('data-task-id');
  if (self) return self;
  const nested = item.querySelector('[data-task-id]')?.getAttribute('data-task-id');
  if (nested) return nested;
  const closest = item.closest('[data-task-id]')?.getAttribute('data-task-id');
  return closest || null;
}

/** Visible order from the DOM first, then remaining same-status tasks (e.g. hidden by filters). */
export function buildFullOrderedIdsForStatus(
  status: TaskStatus,
  listEl: HTMLElement,
  tasksForReorder: ITask[],
): string[] {
  const rawVisible = collectTaskIdsFromListEl(listEl);
  /**
   * After a cross-column drag, the source column’s DOM can still list the moved row for a tick
   * while `tasksForReorder` already has the new `status`. Counting that id would send N+1 ids for
   * a column that only has N tasks server-side → "Invalid task order".
   */
  const visibleIds = rawVisible.filter((id) => {
    const t = tasksForReorder.find((x) => x.id === id);
    return t != null && t.status === status;
  });
  const allInStatus = tasksForReorder.filter((t) => t.status === status);
  const visSet = new Set(visibleIds);
  const rest = allInStatus.filter((t) => !visSet.has(t.id));
  rest.sort(compareTasksByStatusOrder);
  const merged = [...visibleIds, ...rest.map((t) => t.id)];
  const seen = new Set<string>();
  return merged.filter((id) => {
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export function resolveListStatus(el: HTMLElement | null): TaskStatus | null {
  if (!el) return null;
  const values = Object.values(TaskStatus) as string[];
  const direct = el.getAttribute('data-status');
  if (direct && values.includes(direct)) {
    return direct as TaskStatus;
  }
  const closest = el.closest('[data-status]')?.getAttribute('data-status');
  if (closest && values.includes(closest)) {
    return closest as TaskStatus;
  }
  return null;
}
